import { useSubscription } from "@apollo/client";
import { DateTime } from "luxon";
import { AppointmentListFragment, PageInfo } from "@/gql/graphql";
import { APPOINTMENT_SUBSCRIPTION } from "@/graphql/subscriptions";
import { timezoneUtils } from "@/lib/timezone";

// Types
type AppointmentDate = string | Date;
type Edge = { node: AppointmentListFragment; cursor: string };
type CacheData = {
  edges: Edge[];
  pageInfo: PageInfo;
};

/**
 * Parse date string or Date object to luxon DateTime
 */
const parseAppointmentDate = (date: AppointmentDate): DateTime => {
  return typeof date === "string" ? timezoneUtils.fromISO(date) : timezoneUtils.fromJSDate(date);
};

/**
 * Date categorization helpers
 */

/** Check if appointment is scheduled for today in salon timezone */
const _isToday = (appointmentDate: AppointmentDate, salonTimezone: string): boolean => {
  const today = DateTime.now().setZone(salonTimezone).startOf("day");
  const apptDay = parseAppointmentDate(appointmentDate).setZone(salonTimezone).startOf("day");
  return today.equals(apptDay);
};

/** Check if appointment is scheduled for tomorrow or later in salon timezone */
const _isIncoming = (appointmentDate: AppointmentDate, salonTimezone: string): boolean => {
  const tomorrow = DateTime.now().setZone(salonTimezone).plus({ days: 1 }).startOf("day");
  const apptDateTime = parseAppointmentDate(appointmentDate).setZone(salonTimezone);
  return apptDateTime >= tomorrow;
};

/** Check if appointment is waiting for confirmation (PENDING status and today or future) */
const _isWaitingToConfirm = (
  appointment: AppointmentListFragment,
  salonTimezone: string
): boolean => {
  const today = DateTime.now().setZone(salonTimezone).startOf("day");
  const apptDateTime = parseAppointmentDate(appointment.startTime).setZone(salonTimezone);
  return apptDateTime >= today && appointment.bookingStatus === "PENDING";
};

/**
 * Cache management helpers
 */

/**
 * Check if new appointment should be added to cursor-based cache
 * Only add if within currently loaded cursor boundaries
 */
const shouldAddToCursorCache = (
  newAppointment: AppointmentListFragment,
  cacheData: CacheData
): boolean => {
  const currentEdges = cacheData?.edges || [];
  const hasNextPage = cacheData?.pageInfo?.hasNextPage;

  // If all data is loaded, always add
  if (!hasNextPage) return true;

  // If we have more data to load, only add if within current range
  if (currentEdges.length > 0) {
    const lastLoadedAppointment = currentEdges[currentEdges.length - 1].node;
    const newApptTime = parseAppointmentDate(newAppointment.startTime);
    const lastApptTime = parseAppointmentDate(lastLoadedAppointment.startTime);

    return newApptTime <= lastApptTime;
  }

  return true;
};

/** Insert appointment in correct chronological position in edges array */
const insertAppointmentInEdges = (
  edges: Edge[],
  newAppointment: AppointmentListFragment
): Edge[] => {
  const newEdge: Edge = {
    node: newAppointment,
    cursor: newAppointment.id,
  };

  // Find correct chronological position
  const insertIndex = edges.findIndex((edge) => {
    const edgeTime = parseAppointmentDate(edge.node.startTime);
    const newApptTime = parseAppointmentDate(newAppointment.startTime);
    return edgeTime > newApptTime;
  });

  if (insertIndex === -1) {
    return [...edges, newEdge];
  }

  return [...edges.slice(0, insertIndex), newEdge, ...edges.slice(insertIndex)];
};

/** Check if appointment already exists in edges array */
const appointmentExists = (edges: Edge[], appointmentId: string): boolean => {
  return edges.some((edge) => edge.node.id === appointmentId);
};

/** Update cursor-based cache (todaysAppointments, incomingAppointments, etc.) */
const _updateCursorBasedCache = (
  existing: CacheData,
  appointment: AppointmentListFragment,
  shouldInclude: boolean
): CacheData => {
  if (!existing || !shouldInclude) {
    return existing;
  }

  // Check cursor boundaries
  const shouldAdd = shouldAddToCursorCache(appointment, existing);
  if (!shouldAdd) {
    return existing;
  }

  // Check if appointment already exists
  const currentEdges = existing.edges || [];
  if (appointmentExists(currentEdges, appointment.id)) {
    return existing;
  }

  // Add appointment to cache
  const newEdges = insertAppointmentInEdges(currentEdges, appointment);
  return { ...existing, edges: newEdges };
};

/**
 * Update direct appointments cache for CREATE and UPDATE events
 */
const _updateDirectAppointmentsCache = (
  existing: CacheData,
  appointment: AppointmentListFragment,
  isCreateEvent: boolean
) => {
  if (!existing) return existing;

  // Handle cursor-based pagination (edges structure)
  if (existing.edges) {
    const currentEdges = existing.edges || [];

    if (isCreateEvent) {
      // Check if appointment already exists to prevent duplicates
      if (appointmentExists(currentEdges, appointment.id)) return existing;

      // Insert appointment in correct chronological position
      const newEdges = insertAppointmentInEdges(currentEdges, appointment);
      return { ...existing, edges: newEdges };
    }
    // Update existing appointment
    const updatedEdges = currentEdges.map((edge: Edge) =>
      edge.node.id === appointment.id ? { ...edge, node: { ...edge.node, ...appointment } } : edge
    );

    return { ...existing, edges: updatedEdges };
  }

  return existing;
};

/**
 * Hook to subscribe to appointment changes and update Apollo cache in real-time
 *
 * Handles both CREATE and UPDATE events:
 * - CREATE events: Adds new appointments to appropriate cache fields based on date/status
 * - UPDATE events: Updates existing appointments in place
 *
 * Supports cursor boundary checking to prevent cache inconsistencies
 *
 * @param salonId - ID of the salon to subscribe to
 * @param salonTimezone - Timezone of the salon for date calculations
 */
export const useAppointmentSubscription = (salonId: string, _salonTimezone: string = "UTC") => {
  useSubscription(APPOINTMENT_SUBSCRIPTION, {
    variables: {
      filters: { salonId },
    },
    skip: !salonId,
    onData: ({ data: _data, client }) => {
      client.refetchQueries({
        include: [
          "Appointments",
          "WaitingToConfirmAppointments",
          "DashboardMetrics",
          "CalendarAppointments",
        ],
      });
    },
  });
};
